import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ScreenWrapper from '../../components/common/ScreenWrapper';
import { useMutation, useQuery } from '@apollo/client';
import { 
  CreateShopCollectionMutation, 
  CreateShopCollectionMutationVariables,
  RemoveShopCollectionMutation,
  RemoveShopCollectionMutationVariables 
} from '../../graphql/mutations/__generated__/shop.mutation';
import { CREATE_SHOP_COLLECTION, REMOVE_SHOP_COLLECTION } from '../../graphql/mutations/shop.mutation';
import { GET_SHOP_COLLECTIONS } from '../../graphql/queries/shop.query';
import { useAppSelector } from '../../redux/hook';
import { useShop } from '../../hooks/useShop';
import ImageUploader from "../../components/common/ImageUpload";
import { toast } from "sonner";

// Custom hook for fetching shop collections
const useShopCollections = (shopId: string | undefined) => {
  const { data, loading, error, refetch } = useQuery(GET_SHOP_COLLECTIONS, {
    variables: { filter: { shop_id: { eq: shopId } }, pagination: { limit: 10, offset: 0 } },
    skip: !shopId,
  });
  
  return { data, loading, error, refetch };
};

function Collection() {
  const { user } = useAppSelector((state) => state.auth);
  const { shop } = useShop(); 
  const [collectionName, setCollectionName] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();
  
  // Ensure shopId is valid
  const shopId = shop?.id;

  // Fetch existing collections
  const { data: collectionsData, loading: fetchLoading, error: fetchError, refetch } = useShopCollections(shopId);

  // Mutation for creating new collections
  const [createCollection, { loading: createLoading, error: createError }] = useMutation<
    CreateShopCollectionMutation,
    CreateShopCollectionMutationVariables
  >(CREATE_SHOP_COLLECTION, {
    refetchQueries: [{ query: GET_SHOP_COLLECTIONS, variables: { filter: { shop_id: { eq: shopId } }, pagination: { limit: 10, offset: 0 } } }],
    onCompleted: () => {
      refetch();
      resetForm();
      toast.success("Collection created successfully!");
    },
    onError: (error) => {
      toast.error(`Failed to create collection: ${error.message}`);
    }
  });
  
  // Mutation for removing collections
  const [removeCollection, { loading: removeLoading, error: removeError }] = useMutation<
    RemoveShopCollectionMutation,
    RemoveShopCollectionMutationVariables
  >(REMOVE_SHOP_COLLECTION, {
    refetchQueries: [{ query: GET_SHOP_COLLECTIONS, variables: { filter: { shop_id: { eq: shopId } }, pagination: { limit: 10, offset: 0 } } }],
    onCompleted: () => {
      refetch();
      toast.success("Collection removed successfully!");
    },
    onError: (error) => {
      toast.error(`Failed to remove collection: ${error.message}`);
    }
  });

  const resetForm = () => {
    setCollectionName('');
    setBanner(null);
    setPreviewUrl('');
    setShowAddForm(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadStart = () => {
    setIsUploading(true);
  };
  
  const handleUploadComplete = (url: string) => {
    setBanner(url);
    setIsUploading(false);
  };
  
  const handleUploadError = (errorMessage: string) => {
    toast.error(`Image upload failed: ${errorMessage}`);
    setIsUploading(false);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!collectionName.trim()) {
      toast.warning('Please enter a collection name');
      return;
    }
    
    if (!banner) {
      toast.warning('Please upload a banner image');
      return;
    }
    
    try {
      await createCollection({
        variables: {
          input: {
            name: collectionName,
            shop_id: shopId || "",
            id: user?.id,
            banner: banner,
          },
        },
      });
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
    }
  };
  
  // const handleEditCollection = (collection: any) => {
  //   toast.info("Edit functionality to be implemented");
  // };
  
  const handleDeleteCollection = async (collectionId: string) => {
    try {
      await removeCollection({
        variables: {
          removeShopCollectionId: collectionId,
        },
      });
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
    }
  };
  
  const handleCollectionClick = (collectionId: string) => {
    navigate(`/collection/${collectionId}`);
  };
  
  const filteredCollections = collectionsData?.getShopCollections?.filter(
    (collection: any) => collection.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  
  const isSubmitDisabled = createLoading || isUploading || removeLoading;
  
  return (
    <ScreenWrapper showBackBtn={true}>
      {/* Error state */}
      {(fetchError || createError || removeError) && (
        <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {fetchError?.message || createError?.message || removeError?.message || 'An error occurred'}
        </div>
      )}
      
      {/* Loading state */}
      {fetchLoading ? (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-500">Loading collections...</p>
        </div>
      ) : filteredCollections.length === 0 ? (
        // Empty state
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-gray-50 rounded-lg">
          <h2 className="text-2xl font-bold mb-2">No Collections Yet</h2>
          <p className="text-gray-600 mb-6">Create your first collection to organize your shop items.</p>
          <button 
            onClick={() => setShowAddForm(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded transition duration-200"
          >
            Create Collection
          </button>
        </div>
      ) : (
        // Collections list view
        <div className="max-w-6xl mx-auto md:pt-10">
          <h1 className="text-2xl font-bold mb-6">Your Collections</h1>
          
          <div className="flex justify-center gap-10 items-center mb-6">
            <div className="max-w-md w-full">
              <input
                type="text"
                placeholder="Search collections..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded"
              />
            </div>
            
            <button 
              onClick={() => setShowAddForm(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded flex items-center transition duration-200"
            >
              <span className="mr-1">+</span> Add Collection
            </button>
          </div>
          
          {/* Collections list */}
          <div className="space-y-3">
            {filteredCollections.map((collection: any) => (
              <div 
                key={collection.id} 
                className="flex justify-between items-center p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition duration-200"
                onClick={() => handleCollectionClick(collection.id)}
              >
                <div className="flex items-center space-x-4">
                  {collection.banner && (
                    <img 
                      src={collection.banner} 
                      alt={collection.name} 
                      className="w-16 h-16 object-cover rounded" 
                    />
                  )}
                  <div>
                    <h3 className="font-medium">{collection.name}</h3>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {/* <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditCollection(collection);
                    }}
                    className="p-2 text-gray-500 hover:text-blue-500 hover:bg-gray-100 rounded"
                  >
                    Edit
                  </button> */}
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCollection(collection.id);
                    }}
                    className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-100 rounded"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Add Collection Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center border-b p-4">
              <h2 className="text-xl font-semibold">Create New Collection</h2>
              <button 
                className="text-gray-500 hover:text-gray-700 text-2xl font-light"
                onClick={resetForm}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            {/* Collection Form */}
            <form onSubmit={handleSubmit} className="p-4">
              <div className="mb-4">
                <label htmlFor="collectionName" className="block font-medium mb-2">
                  Collection Name
                </label>
                <input
                  type="text"
                  id="collectionName"
                  value={collectionName}
                  onChange={(e) => setCollectionName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter collection name"
                />
              </div>
              
              <div className="mb-6">
                <label className="block font-medium mb-2">
                  Banner Image
                </label>
                <ImageUploader 
                  onUpload={handleUploadComplete} 
                  onError={handleUploadError}
                  onUploadStart={handleUploadStart}
                  label="Upload Banner" 
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button 
                  type="button" 
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                  disabled={isSubmitDisabled}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className={`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 ${isSubmitDisabled ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={isSubmitDisabled}
                >
                  {createLoading ? 'Creating...' : isUploading ? 'Uploading...' : 'Create Collection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </ScreenWrapper>
  );
}

export default Collection;